import { Injectable } from '@angular/core';
import { IndexedDBService } from '../database/indexeddb.service';
import * as LZString from 'lz-string';

@Injectable({
  providedIn: 'root'
})
export class UndoRedoService {

  private history: any[] = [];
  private historyIndex = -1;
  private MAX_HISTORY = 10;

  /**
   * When undo/redo is triggered, the component stores the target JSON here
   * before destroying itself. On re-mount, ngOnInit picks this up and uses
   * it as the initial stationData instead of the @Input value.
   * `null` means it is a normal (initial) load.
   */
  pendingUndoState: any | null = null;

  /**
   * Set to true by undo/redo so that the newly re-mounted component instance
   * triggers a background server save in ngAfterViewInit with the restored JSON.
   */
  pendingSaveAfterUndoRedo: boolean = false;

  /**
   * Set to true by undo/redo so that the newly re-mounted component instance
   * restores edit mode in ngOnInit regardless of the parent's editStatus input.
   */
  preserveEditMode: boolean = false;

  /**
   * Unsaved-changes baseline to hand to the re-mounted instance. A re-mount that
   * restores edit mode via `preserveEditMode` never runs applyEditMode(), so it
   * would otherwise start with a null snapshot and keep "Save for later" disabled.
   * Set to the pre-operation layout so the adjusted layout reads as unsaved.
   */
  pendingEditBaseline: any | null = null;

  /**
   * When a normal save triggers a component re-mount (via refreshStationFiles),
   * the outgoing instance stores the full history here so the new instance can
   * restore it — preserving the undo/redo stack across the save cycle.
   * `null` means this is a fresh initial load.
   */
  pendingHistoryTransfer: { history: any[]; index: number } | null = null;
  versionChanged: boolean = false;

  constructor(private indexDB: IndexedDBService) { }

  init(state: any) {
    this.indexDB.getSnapshots(state?.['Station Uuid']).then((snapshots) => {
      for(let data of snapshots ?? []) {
        this.history.push(JSON.parse(LZString.decompressFromBase64(data.snapshot)!));
        this.historyIndex++;
      }
      this.history.push(structuredClone(state));
      this.historyIndex++;
      
    });
  }

  saveState(state: any) {
    const clone = structuredClone(state);
    // Truncate any future states if we branched from a middle point
    if (this.historyIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyIndex + 1);
    }
    this.history.push(clone);
    // Always increment first, then trim the oldest if over limit
    this.historyIndex++;
    if (this.history.length > this.MAX_HISTORY) {
      this.history.shift();
      this.historyIndex--; // adjust pointer after removing oldest
    }
        
  }

  undo(): any | null {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      return this.history[this.historyIndex];
    }
    return null;
  }

  redo(): any | null {
    if (this.historyIndex < this.history.length - 1) {
      this.historyIndex++;
      return this.history[this.historyIndex];
    }
    return null;
  }

  canUndo(): boolean {
    return this.historyIndex > 0;
  }

  canRedo(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  /** Returns the current position in the history stack (0-based). */
  getHistoryIndex(): number {
    return this.historyIndex;
  }

  /** Snapshot the full history for transfer across a component re-mount. */
  exportHistory(): { history: any[]; index: number } {
    return { history: structuredClone(this.history), index: this.historyIndex };
  }

  /** Restore history from a snapshot produced by exportHistory(). */
  restoreHistory(snapshot: { history: any[]; index: number }): void {
    this.history = snapshot.history;
    this.historyIndex = snapshot.index;
  }

  clear() {
    this.history = [];
    this.historyIndex = -1;
  }

  /**
   * Full reset: wipes history AND all pending transfer/state fields.
   * Call this when navigating away from a station (e.g. back to Home)
   * so that if a new layout is generated for the same station UUID the
   * next time the user opens it, the undo stack starts completely fresh.
   */
  clearAll(stationId: string) {
    this.history = [];
    this.historyIndex = -1;
    this.pendingUndoState = null;
    this.pendingHistoryTransfer = null;
    this.pendingSaveAfterUndoRedo = false;
    this.preserveEditMode = false;
    this.pendingEditBaseline = null;
    this.indexDB.clearSnapshots(stationId);
  }
}
